// Which network this handset is plugged into.
//
// Kevin, 2026-08-10: "a device can have two lines, one on pbx-01 fwa and the other on pbx-02
// fwa. In the case of the test account it only is pbx-02 fwa and all three lines should
// point to it."
//
// That is why the setting is HERE and not on each line. The segment is one physical fact per
// handset — the wire it is on — and it is the LINE that brings the other half, its PBX host.
// Each line then resolves its own host's interface on this segment, so one setting can send
// a PBX-01 line and a PBX-02 line to two different addresses without anybody saying "FWA"
// twice. Set it once here and the test account's three lines all follow.
//
// A Verso-only write. Changing it does not push anything: the affected lines start showing a
// pending change, and it goes out through Save and Apply like every other change.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

export default function DeviceSegmentCard({ device, canEdit }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const current = device?.segmentId ?? null;
  const [choice, setChoice] = useState(current == null ? '' : String(current));

  const segQ = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get('/segments'),
  });

  const save = useMutation({
    mutationFn: (segmentId) => api.put(`/devices/${device.id}/segment`, { segment_id: segmentId }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['device'] });
      // Every line on this handset now resolves to a different interface.
      qc.invalidateQueries({ queryKey: ['line'] });
      qc.invalidateQueries({ queryKey: ['lines'] });
    },
  });

  const segments = segQ.data?.segments || [];
  const chosen = segments.find((s) => String(s.id) === String(current));

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Network</h2>
          <p className="mt-1 text-sm text-gray-500">
            Which network this handset is plugged into. Every line on it uses this, and each
            one reaches its own PBX at that PBX&rsquo;s address on this network.
          </p>
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => { save.reset(); setChoice(current == null ? '' : String(current)); setEditing(true); }}
            className="flex-shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Change
          </button>
        )}
      </div>

      {!editing && (
        <div className="mt-4 text-sm">
          {chosen ? (
            <>
              <p className="font-medium text-gray-900">{chosen.name}</p>
              {chosen.description && (
                <p className="mt-0.5 text-xs text-gray-500">{chosen.description}</p>
              )}
            </>
          ) : current != null ? (
            // Names a segment the list does not contain — not the same as "not set", and
            // rendering it as such would hide a real problem.
            <p className="text-amber-800">
              Set to a network that is no longer listed ({String(current)}). Choose one.
            </p>
          ) : (
            <p className="text-gray-600">
              Not set — lines on this handset use their PBX&rsquo;s default address, which is
              right unless it sits on a private network like FWA or fibre.
            </p>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-4">
          {segQ.isLoading && <p className="text-sm text-gray-500">Loading networks…</p>}
          {segQ.error && (
            <p className="text-sm text-red-700">
              Could not load networks: {segQ.error.body?.error || segQ.error.message}
            </p>
          )}
          {!segQ.isLoading && !segQ.error && (
            <>
              <label htmlFor="dev-segment" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Network
              </label>
              <select
                id="dev-segment"
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Not set — use each PBX&rsquo;s default</option>
                {segments.map((sg) => (
                  <option key={sg.id} value={sg.id}>
                    {sg.name}{sg.description ? ` · ${sg.description}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Saved to Verso only. The lines on this handset will show it as a pending
                change until you apply them.
              </p>
            </>
          )}

          {save.isError && (
            <p className="mt-3 text-sm text-red-700">{save.error.body?.error || save.error.message}</p>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate(choice === '' ? null : Number(choice))}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); save.reset(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
